import { useEffect, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Calendar, Tag } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import CtaBand from "./CtaBand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    setState("loading");
    axios
      .get(`${API}/articles/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        setState("ok");
      })
      .catch(() => setState("notfound"));
  }, [slug]);

  useSeo({
    title: article ? `${article.meta_title || article.title} | SoftPro` : "Blog | SoftPro",
    description: article?.meta_description,
  });

  if (state === "loading") {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-navy" />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-4 text-center">
        <div data-testid="article-notfound">
          <h1 className="font-display text-3xl font-bold text-ink">Artikel niet gevonden</h1>
          <p className="mt-3 text-muted-foreground">Dit artikel bestaat niet (meer).</p>
          <button
            onClick={() => navigate("/blog")}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 font-semibold text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Terug naar blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="article-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-deep grain pt-36 pb-16 sm:pt-44 sm:pb-20">
        {article.cover_image && (
          <div className="absolute inset-0 z-0">
            <img src={article.cover_image} alt={article.title} className="h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/80 to-navy-deep/50" />
          </div>
        )}
        <div className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-teal transition-colors">
            <ArrowLeft className="h-4 w-4" /> Terug naar blog
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/60">
            <span className="inline-flex items-center gap-1.5">
              <Tag className="h-4 w-4 text-teal" /> {article.category}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-teal" /> {fmtDate(article.published_at)}
            </span>
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight text-white leading-[1.05]">
            {article.title}
          </h1>
        </div>
      </section>

      {/* Body */}
      <article className="bg-background py-16 sm:py-20">
        <div
          className="article-prose mx-auto max-w-3xl px-4 sm:px-6"
          data-testid="article-content"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(article.content_html || "", {
              ALLOWED_TAGS: ["h2", "h3", "h4", "p", "ul", "ol", "li", "strong", "em", "a", "br", "blockquote"],
              ALLOWED_ATTR: ["href", "target", "rel"],
            }),
          }}
        />
      </article>

      <CtaBand />
    </div>
  );
};

export default ArticlePage;
